/**
 * The sub-nav shared by the four *Accesos* screens.
 *
 * WHY IT IS A COMPONENT AND NOT A LAYOUT ROUTE
 * -------------------------------------------
 * `.github/scripts/check_guide_keys.py::routes()` regex-reads
 * `path="…"` out of each `<Route>` tag, and assertion F only inspects
 * paths that start with `/admin/`. Written as relative children of a
 * pathless layout route (`path="scopes"`), these four screens become
 * INVISIBLE to the gate — J3 would silently open the hole the gate
 * exists to close — and assertion G would then fail because
 * `SCREEN_HELP_ROUTES` names the absolute path.
 *
 * So every route is absolute and the shared chrome is this component,
 * which each page renders. It is the `ProjectHubPage` pattern.
 *
 * It navigates with `<Link>` rather than a `Tabs` + `useNavigate` pair
 * so the entries are real anchors: middle-click and "open in new tab"
 * work, and a screen reader announces a link to another page instead of
 * a tab within this one — which is what they are.
 */

import { cx } from "@pervasivemind/admin-ui";
import { Link, useLocation } from "react-router-dom";

import { useT } from "@/i18n";

export const ACCESS_TABS = [
  { id: "scopes", to: "/admin/access/scopes", key: "access.tabs.scopes" },
  { id: "groups", to: "/admin/access/groups", key: "access.tabs.groups" },
  { id: "people", to: "/admin/access/people", key: "access.tabs.people" },
  { id: "audit", to: "/admin/access/audit", key: "access.tabs.audit" },
] as const;

export type AccessTabId = (typeof ACCESS_TABS)[number]["id"];

export function AccessTabs({ active }: { active: AccessTabId }): JSX.Element {
  const { t } = useT();
  const location = useLocation();
  return (
    <nav
      aria-label={t("access.tabs.label")}
      className="mb-5 flex gap-1 border-b border-neutral-200"
    >
      {ACCESS_TABS.map((tab) => {
        const current = tab.id === active;
        return (
          <Link
            key={tab.id}
            to={tab.to}
            state={location.state}
            aria-current={current ? "page" : undefined}
            /* The active tab is a ring-free 2 px underline plus weight,
               never a colour alone: the product owner has red-green
               colour blindness, so state is carried by position, weight
               and `aria-current` as well. */
            className={cx(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              current
                ? "border-primary font-semibold text-primary-ink"
                : "border-transparent font-medium text-neutral-600 hover:bg-neutral-50",
            )}
          >
            {t(tab.key)}
          </Link>
        );
      })}
    </nav>
  );
}
